import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const hasProcessed = useRef(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token');
      return;
    }

    const verify = async () => {
      try {
        const { data } = await axios.post(`${API}/auth/verify-email`, { token });
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
      } catch (e) {
        setStatus('error');
        const detail = e.response?.data?.detail;
        setMessage(typeof detail === 'string' ? detail : 'Verification failed');
      }
    };

    verify();
  }, [searchParams, API]);

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Card className="p-8 border border-stone-200 text-center">
          {status === 'verifying' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C53030] mx-auto mb-4"></div>
              <h2 className="text-xl sm:text-2xl text-[#1C1917] mb-2">Verifying your email...</h2>
              <p className="text-sm text-stone-600">Please wait a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="w-16 h-16 text-green-600" />
              </div>
              <h2 className="text-xl sm:text-2xl text-[#1C1917] mb-2">Email verified!</h2>
              <p className="text-base text-stone-800 leading-relaxed mb-6">{message}</p>
              <Button
                data-testid="verify-continue-button"
                onClick={() => navigate('/dashboard')}
                className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md"
              >
                Continue to dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <XCircle className="w-16 h-16 text-red-600" />
              </div>
              <h2 className="text-xl sm:text-2xl text-[#1C1917] mb-2">Verification failed</h2>
              <p className="text-base text-stone-800 leading-relaxed mb-6">{message}</p>
              <Button
                data-testid="verify-retry-button"
                onClick={() => navigate('/login')}
                variant="outline"
                className="border border-stone-200"
              >
                Back to login
              </Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
